import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, TextInput, Text, ActivityIndicator, Platform, Pressable,
} from 'react-native';
import Colors from '../../../constants/Colors';
import Button from '../../../components/Button';
import addTicketComment from '../../../requests/Tickets/AddTicketComment';
import Style from '../../../constants/Style';
import { generateAvatarColor } from '../data';
import { getInitials } from './TicketAssigneeIcon';

const MAX_LENGTH: number = 300;

interface CommentsPaneProps {
  id: string,
  updateSelectedTicketInList: Function,
  name: string,
}

const CommentsPane = ({ id, updateSelectedTicketInList, name }: CommentsPaneProps) => {
  const [value, onChangeText] = useState<string>('');
  const [editorFocused, setEditorFocused] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);

  useEffect(() => {
    onChangeText('');
    setEditorFocused(false);
    setSending(false);
  }, [id]);

  useEffect(() => setSent(false), [value]);

  const onFocus = () => setEditorFocused(true);
  const onBlur = () => setEditorFocused(false);

  const sendComment = () => {
    setSending(true);
    addTicketComment(id, value).then((res) => {
      if (res && !res.isError) {
        updateSelectedTicketInList(res);
        onChangeText('');
        setEditorFocused(false);
        setSent(true);
      }
      setSending(false);
    }).catch(() => setSending(false));
  };

  return <Pressable>
    <View style={styles.container}>
      {editorFocused && <Text style={styles.characterCount}>
        {`${value?.length ?? 0}/${MAX_LENGTH}`}
      </Text>}
      <View style={styles.content}>
        <View style={[
          styles.profileIcon, { backgroundColor: generateAvatarColor(name?.length ?? 5) },
        ]}>
          <Text style={styles.name}>
            {getInitials(name)}
          </Text>
        </View>
        <TextInput
          style={[styles.textInput, editorFocused && styles.textInputFocused]}
          placeholder='Add an internal comment'
          {...{
            value, onChangeText, onFocus, onBlur,
          }}
          maxLength={MAX_LENGTH}
          placeholderTextColor={Colors.light.hereworksBlack400}
          multiline
        />
      </View>
      <View style={[Style.row, { alignSelf: 'stretch' }]}>
        {sent && <Text style={styles.text}>{'Comment added successfully'}</Text>}
        {(value?.length > 0 || editorFocused) && <View style={styles.buttonContainer}>
          <Button
            title='Cancel'
            style={styles.button}
            onPress={() => onChangeText('')}
          />
          <Button
            title={sending ? '' : 'Save'}
            style={styles.button}
            affirmative
            disabled={sending || !(value?.length > 0)}
            children={sending ? <ActivityIndicator /> : null}
            onPress={sendComment}
            testID='save-comment-btn'
          />
        </View>}
      </View>
    </View>
  </Pressable>;
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 16,
  },
  characterCount: {
    alignSelf: 'flex-end',
    fontSize: 12,
    lineHeight: Platform.OS === 'ios' ? undefined : 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack300,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  profileIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.light.hereworksBlack400,
    marginEnd: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  textInput: {
    flex: 1,
    height: 40,
    paddingVertical: 10,
    paddingStart: 8,
    paddingEnd: 24,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue400,
    backgroundColor: '#FFF',
    color: Colors.light.hereworksBlack600,
    verticalAlign: 'top',
  },
  textInputFocused: {
    height: 88,
  },
  editor: {
    flex: 1,
    height: 88,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  button: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    flex: undefined,
  },
  text: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksPrimaryGrey700,
    alignSelf: 'flex-start',
  },
  name: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: '#FFF',
  },
});

export default CommentsPane;
